import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import type { ReportSnapshotSummaryDto } from "@/api/dtos";
import { ReportTags } from "./ReportTags";
import type { ReportSnapshotSummary, ReportUploadKind } from "./_models/ReportUpload";
import { toReportSnapshotSummaryFromDto } from "./_transformers/toReportSnapshotSummaryFromDto";

export interface GetReportSnapshotArgs {
  report: ReportUploadKind;
  /** yyMMdd */
  date: string;
}

/** A report's built snapshot for one day (GET /reports/{report}/snapshots/{yyMMdd}); null when not built (404). */
export const getReportSnapshotApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getReportSnapshot: builder.query<ReportSnapshotSummary | null, GetReportSnapshotArgs>({
      queryFn: async (args) => {
        try {
          const res = await axios.get<ReportSnapshotSummaryDto>(
            `${backendBase()}/reports/${args.report}/snapshots/${args.date}`,
            { headers: { Accept: "application/json" } },
          );
          const snapshot = toReportSnapshotSummaryFromDto(res.data);
          return { data: snapshot };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response?.status === 404) {
            return { data: null };
          }
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
      providesTags: (_result, _error, args) => [{ type: ReportTags.ReportSnapshot, id: `${args.report}-${args.date}` }],
    }),
  }),
});

export const getReportSnapshot = getReportSnapshotApi.endpoints.getReportSnapshot;
